// =====================================================================
// WorkingHoursEditor — editor amigavel pro campo schedule_json
// (clinic_professionals.schedule_json). Matrix 7 dias x N slots.
//
// CONVENCAO do JSON (DEFINIDA AQUI — Fase 3, S38):
//   {
//     "monday":    [{ "start": "08:00", "end": "12:00" }, { "start": "14:00", "end": "18:00" }],
//     "tuesday":   [{ "start": "08:00", "end": "12:00" }],
//     ...,
//     "saturday":  [],
//     "sunday":    []
//   }
// Dias em ingles minusculo (monday..sunday). Horarios HH:MM (24h).
// Dia sem slots = nao trabalha. Slots NAO precisam estar ordenados.
//
// O workflow Admin v2 valida que schedule_json e objeto JSON (nao array).
// Validacao semantica fica aqui (cliente).
// =====================================================================
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  IconButton,
  TextField,
  Typography,
  Switch,
  Button,
  Tooltip
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";

const DAYS = [
  { key: "monday",    label: "Segunda" },
  { key: "tuesday",   label: "Terça"   },
  { key: "wednesday", label: "Quarta"  },
  { key: "thursday",  label: "Quinta"  },
  { key: "friday",    label: "Sexta"   },
  { key: "saturday",  label: "Sábado"  },
  { key: "sunday",    label: "Domingo" }
];

const useStyles = makeStyles((theme) => ({
  root: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1)
  },
  dayRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderBottom: `1px dashed ${theme.palette.divider}`,
    flexWrap: "wrap",
    "&:last-child": { borderBottom: "none" }
  },
  dayLabel: {
    minWidth: 100,
    fontWeight: 600,
    fontSize: "0.875rem",
    paddingTop: theme.spacing(1)
  },
  slotsCol: {
    flex: 1,
    minWidth: 280,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75)
  },
  slotRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  timeInput: {
    width: 110,
    "& input": { padding: "8px" }
  },
  inactiveHint: {
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    fontStyle: "italic"
  },
  addSlotBtn: {
    fontSize: "0.75rem",
    textTransform: "none",
    alignSelf: "flex-start",
    padding: "2px 8px"
  },
  copyBtn: {
    padding: 6
  }
}));

// Cria um value vazio se vier null/undefined
const normalize = (value) => {
  const out = {};
  DAYS.forEach((d) => {
    const v = value?.[d.key];
    if (Array.isArray(v)) {
      out[d.key] = v
        .filter((s) => s && typeof s === "object")
        .map((s) => ({ start: s.start || "08:00", end: s.end || "18:00" }));
    } else {
      out[d.key] = [];
    }
  });
  return out;
};

const WorkingHoursEditor = ({ value, onChange }) => {
  const classes = useStyles();
  const data = normalize(value);

  const setDay = (dayKey, slots) => {
    onChange({ ...data, [dayKey]: slots });
  };

  const toggleDay = (dayKey, on) => {
    if (on) {
      // Liga: 1 slot default 08:00-18:00
      setDay(dayKey, [{ start: "08:00", end: "18:00" }]);
    } else {
      setDay(dayKey, []);
    }
  };

  const addSlot = (dayKey) => {
    const cur = data[dayKey];
    const last = cur[cur.length - 1];
    // Sugere proximo slot: comeca onde o ultimo terminou
    const defaultStart = last?.end || "14:00";
    setDay(dayKey, [...cur, { start: defaultStart, end: "18:00" }]);
  };

  const removeSlot = (dayKey, idx) => {
    const cur = data[dayKey];
    setDay(dayKey, cur.filter((_, i) => i !== idx));
  };

  const updateSlot = (dayKey, idx, field, val) => {
    const cur = data[dayKey];
    const next = cur.map((s, i) => (i === idx ? { ...s, [field]: val } : s));
    setDay(dayKey, next);
  };

  const copyToAllWeekdays = (sourceDayKey) => {
    const src = data[sourceDayKey];
    if (!src || src.length === 0) return;
    const weekdays = ["monday", "tuesday", "wednesday", "thursday", "friday"];
    const next = { ...data };
    weekdays.forEach((k) => {
      next[k] = src.map((s) => ({ ...s }));
    });
    onChange(next);
  };

  return (
    <div className={classes.root}>
      {DAYS.map((d) => {
        const slots = data[d.key];
        const active = slots.length > 0;
        return (
          <Box key={d.key} className={classes.dayRow}>
            <Typography className={classes.dayLabel}>{d.label}</Typography>
            <Switch
              checked={active}
              onChange={(e) => toggleDay(d.key, e.target.checked)}
              size="small"
              color="primary"
            />
            <div className={classes.slotsCol}>
              {!active && (
                <Typography className={classes.inactiveHint}>
                  Não trabalha
                </Typography>
              )}
              {slots.map((s, idx) => (
                <div key={idx} className={classes.slotRow}>
                  <TextField
                    type="time"
                    variant="outlined"
                    size="small"
                    value={s.start}
                    onChange={(e) =>
                      updateSlot(d.key, idx, "start", e.target.value)
                    }
                    className={classes.timeInput}
                    inputProps={{ step: 300 /* 5min */ }}
                  />
                  <Typography variant="caption">até</Typography>
                  <TextField
                    type="time"
                    variant="outlined"
                    size="small"
                    value={s.end}
                    onChange={(e) =>
                      updateSlot(d.key, idx, "end", e.target.value)
                    }
                    className={classes.timeInput}
                    inputProps={{ step: 300 }}
                  />
                  <IconButton
                    size="small"
                    title="Remover slot"
                    onClick={() => removeSlot(d.key, idx)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                  {idx === 0 && d.key !== "saturday" && d.key !== "sunday" && (
                    <Tooltip title="Copiar este dia pra seg a sex">
                      <IconButton
                        size="small"
                        className={classes.copyBtn}
                        onClick={() => copyToAllWeekdays(d.key)}
                      >
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              ))}
              {active && (
                <Button
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => addSlot(d.key)}
                  className={classes.addSlotBtn}
                >
                  Adicionar intervalo
                </Button>
              )}
            </div>
          </Box>
        );
      })}
    </div>
  );
};

export default WorkingHoursEditor;
